import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  cloneElement,
  useMemo,
} from 'react';
import { _t } from 'app/utils/i18n';
import { Grid } from '@material-ui/core';
import {
  CancelIcon,
  CountryIcon,
  DateFrom,
  DateTo,
  HashtagsIcon,
  PlatformIcon,
  RestartIcon,
  SaveIcon,
} from 'app/assets/images/icons';
import { format } from 'date-fns';

import SelectInput from '../../Form/Inputs/SelectInput';
import MainFilter from '../../MainFilter';
import DataGrid from '@inovua/reactdatagrid-enterprise';
import '@inovua/reactdatagrid-enterprise/index.css';
import styles from './PrimaryMainTable.module.scss';
import { useHistory, useRouteMatch } from 'react-router';
import useFilters from 'app/hooks/useFilters';
import { ResponseType } from 'app/utils/types';
import Loader from 'app/components/Loader';
import { getFromLocalStorage, setInLocalStorage } from 'app/utils/localStorage';
import { apiRoutes } from 'trends/utils/apiRoutes';
import { openConfirmationPopup } from 'app/utils/confirmationPopup';

const ReactDataGrid = DataGrid as any;

import {
  bulkActionMap,
  CustomCheckbox,
  emptyText,
  renderSortTool,
  checkboxColumn,
  renderDefaultHeader,
  getTableMinHeight,
} from './components';
import { TableControls, TablePagination } from './components/index';

import { toast } from 'react-toastify';
import { getModuleName } from 'influencers/utils/getModuleName';
import {
  bulkAction,
  checkBulkAction,
  deleteBulkAction,
  loadTableData,
  partialSyncBulkAction,
  regrabBulkAction,
  syncBulkAction,
} from 'app/Requests';
import { useMutation, useQueryClient } from 'react-query';
import { useModal } from 'app/context/Modal';
import { useAuth } from 'app/context/auth';
import {
  checkToShowCreatedAt,
  handleCleanObjFromFalsyValues,
} from 'app/utils/helpers';
import { IMainTable } from 'app/components/MainTableMultiTabs/types';
import { isThereASelectedColumn } from 'app/utils/key-value-check';
import { getSearchState, setSearchState } from 'app/utils/search-state';
import useBrowserBack from 'app/hooks/use-browser-back';
import {
  dateTimeFormatter,
  fromLocalToServerTimeZone,
} from 'app/utils/dateTimeFormatter';
import ToolTipComp from 'app/components/ToolTipComp';

const initialState = {
  hashtag: '',
  country_ids: null,
  platform_ids: null,
  end_date: null,
  start_date: null,
  page: 1,
};
const isEn = getFromLocalStorage('LNG') == 'en' ? true : false;

const PrimaryMainTable: FunctionComponent<IMainTable> = forwardRef(
  (
    {
      tabs = [_t('trends:sets'), _t('trends:trashed')],
      treeColumn,
      parentPassedData,
      stopLoadingFirstMount = false,
      children,
      moduleName,
      columns,
      isFiltered,
      moduleFilters,
      compareUrl,
      initialFilters = initialState,
      customRequiredFilters = {},
      hideCheckbox,
      title,
      hideTopBar,
      defaultSortInfo,
      hideAllTap,
      setActiveTab,
      activeTab,
      hideBulkActions,
      bulkActionOpts = ['compare', 'unnecessary', 'trash'],
      bulkActionsKey,
      actionModule,
      sumOfActionModule,
      removeFilterMr,
      removeTopPadding,
      tabBulkActionPopup,
      bulkActionMethod,
      currentTabRef,
      bulkActionCallback,
      scrollToTopOfTableOnly,
      initialFiltersKeys,
      initFilter,
      defaultTab,
      compId,
      bulkFilter,
      loadDataCallback,
      hideLoader,
      stopSearchFilters,
      hidePagination,
      isLoading,
      rowsLimit,
      additionalComponents,
      grid,
      hideIfNoData,
      fastReportBulkActionHandler,
      showRegrabIcon,
      regrabIconFunction,
      regrabIconTooltipTitle,
    },
    ref,
  ) => {
    const history = useHistory();
    const modal = useModal();
    const queryClient = useQueryClient();
    const countriesOptions = queryClient.getQueryData('getCountries');
    const platforms = queryClient.getQueryData('getPlatforms') as any;
    const { user } = useAuth();
    const loadTableMutate = useMutation(loadTableData);
    const bulkActionMutate = useMutation(bulkAction);
    const regrabBulkActionMutation = useMutation(regrabBulkAction);
    const syncBulkActionMutation = useMutation(syncBulkAction);
    const checkBulkActionMutation = useMutation(checkBulkAction);
    const partialSyncBulkActionMutation = useMutation(partialSyncBulkAction);
    const deleteBulkActionMutate = useMutation(deleteBulkAction);

    const {
      filters,
      onChangeFilter,
      formatFilters,
      resetFilters,
      handleGetDates,
    } = useFilters({
      ...initialFilters,
      ...initFilter,
    });
    const [filtersChanged, setFiltersChanged] = useState(0);

    const { path } = useRouteMatch();
    const [tableKey, setTableKey] = useState('0');
    const baseTableName = `${moduleName}_${path}_${activeTab}`;

    const [draggable, setDraggable] = useState(false);
    const currentPage = useRef(1);
    const tableRef = useRef<any>(null);
    const [isEditModeOn, setIsEditModeOn] = useState(false);
    const lastHeightReached = useRef<number>(860);
    const submitFiltersCounter = useRef<number>(0);

    const [limit, setLimit] = useState<any>(() => {
      const tableLimit =
        rowsLimit || getFromLocalStorage(`${baseTableName}_limit`);
      if (tableLimit) return tableLimit;
      else {
        setInLocalStorage(`${baseTableName}_limit`, rowsLimit ? rowsLimit : 10);
        return rowsLimit ? rowsLimit : 10;
      }
    });

    const [tableMode, setTableOpts] = useState(() => {
      const tableMode = getFromLocalStorage(`${baseTableName}_table_mode`);
      if (tableMode) return tableMode;
      else {
        setInLocalStorage(`${baseTableName}_table_mode`, 'view');
        return 'view';
      }
    });
    const [currentModule] = useState(() =>
      getModuleName(moduleName ? moduleName : '', 0),
    );
    const [recentColumns] = useState(columns);

    const [showedColumns, setShowedColumns] = useState(() => {
      const columnsVisibilityLocalStorage = getFromLocalStorage(
        `${baseTableName}_columns_visibility`,
      );
      if (columnsVisibilityLocalStorage) {
        return columnsVisibilityLocalStorage;
      } else {
        const normalColumnsVisibility = recentColumns?.reduce(
          (acc: any, column: any) => {
            return {
              ...acc,
              [column.name]:
                column.name === 'created_at' &&
                checkToShowCreatedAt(baseTableName)
                  ? false
                  : true,
            };
          },
          {},
        );
        setInLocalStorage(
          `${baseTableName}_columns_visibility`,
          normalColumnsVisibility,
        );
        return normalColumnsVisibility;
      }
    });
    const [showedColumnsBackup, setShowedColumnsBackup] = useState(
      () => showedColumns,
    );
    const [defaultOrder] = useState(() => {
      const columnsOrderLocalStorage = getFromLocalStorage(
        `${baseTableName}_columns_order`,
      );
      if (columnsOrderLocalStorage) {
        return columnsOrderLocalStorage;
      } else {
        const normalOrder = columns?.map(
          (column: { name: any }) => column.name,
        );
        setInLocalStorage(`${baseTableName}_columns_order`, normalOrder);
        return normalOrder;
      }
    });
    const [columnOrder, setColumnOrder] = useState<any>(() => ({
      currentOrder: defaultOrder,
      oldOrder: defaultOrder,
    }));
    const [sortInfo, setSortInfo] = useState<any>({});
    const [selected, setSelected] = useState<any>({});
    const [columnsSizes, setColumnsSizes] = useState(
      getFromLocalStorage(`${baseTableName}_columns_sizes`),
    );
    const [currentSizes] = useState<any[]>(
      getFromLocalStorage(`${baseTableName}_columns_sizes`),
    );

    const [activeColumns] = useState<any>(
      currentSizes?.length
        ? columns?.map(
            (item: { name: any; defaultWidth: any; defaultFlex: any }) => ({
              ...item,
              defaultWidth:
                currentSizes?.find((i: any) => i?.name === item?.name)?.width ||
                item?.defaultWidth,

              defaultFlex:
                currentSizes?.find((i: any) => i?.name === item?.name)
                  ?.defaultFlex || item?.defaultFlex,
              flex:
                currentSizes?.find((i: any) => i?.name === item?.name)
                  ?.defaultFlex || undefined,
            }),
          )
        : columns,
    );
    const [currentDataSource, setCurrentDataSource] = useState<ResponseType>(
      () => (parentPassedData ? parentPassedData : null),
    );
    const selectedHasKey = (keyExtractor: string) => {
      return Object.keys(selected)
        .map((key) => selected[key]?.[keyExtractor])
        .some((item) => item > 0);
    };

    // const selectedIsProgress = (keyExtractor: string) => {
    //   return Object.keys(selected)
    //     .map((key) => selected[key]?.[keyExtractor])
    //     .some((item) => item?.key === 'INPROGRESS');
    // };
    const selectedALLHasKey = (keyExtractor: string) => {
      return Object.keys(selected)
        .map((key) => selected[key]?.[keyExtractor])
        .every((item) => item > 0);
    };
    const checkUnImportantInfluencer = (selectedItems: any, value: any) => {
      const isImportant = selectedALLHasKey('is_important');
      if (isImportant) {
        bulkActionMutate
          .mutateAsync({
            action: value,
            moduleName: bulkActionsKey,
            ids: selectedItems,
            bulkFilter,
          })
          .then(() => {
            _loadData({ ...filters, ...sortInfo, limit });
            toast.success(
              <div>
                <span>
                  {selectedItems.length == 1
                    ? _t(`basic:unimportant_success`)
                    : _t('basic:the_Selected_items_unimportant')}
                </span>
              </div>,
            );
            bulkActionCallback?.();
          });
      } else {
        toast.error(
          <div>
            <span>
              {selectedItems.length == 1
                ? _t('basic:This_item_unimportant')
                : _t('basic:the_Selected_items_cant_be_unimportant')}
            </span>
          </div>,
        );
      }
    };

    const checkImportantInfluencer = (selectedItems: any, value: any) => {
      const isImportant = selectedHasKey('is_important');
      if (isImportant) {
        toast.error(
          <div>
            <span>
              {selectedItems.length == 1
                ? _t('basic:This_item_important')
                : _t('basic:the_Selected_items_cant_be_important')}
            </span>
          </div>,
        );
      } else {
        bulkActionMutate
          .mutateAsync({
            action: value,
            moduleName: bulkActionsKey,
            ids: selectedItems,
            bulkFilter,
          })
          .then(() => {
            _loadData({ ...filters, ...sortInfo, limit });
            toast.success(
              <div>
                <span>
                  {selectedItems.length == 1
                    ? _t(`basic:important_success`)
                    : _t('basic:the_Selected_items_important')}
                </span>
              </div>,
            );
            bulkActionCallback?.();
          });
      }
    };

    const checkUnnecessaryIsAdd = (selectedItems: any, value: any) => {
      const isAdded = selectedHasKey('analyzed');
      if (isAdded) {
        toast.error(
          <div>
            <span>
              {selectedItems.length == 1
                ? _t('basic:This_item_cant_be_unnecessary')
                : _t('basic:the_Selected_items_cant_be_unnecessary')}
            </span>
          </div>,
        );
      } else {
        openConfirmationPopup(
          modal,
          value,
          actionModule,
          sumOfActionModule,
          async () => {
            document.body.classList.add('no-Events');

            await bulkActionMutate
              .mutateAsync({
                action: value,
                moduleName: bulkActionsKey,
                ids: selectedItems,
                bulkFilter,
              })
              .then(() => {
                _loadData({ ...filters, ...sortInfo, limit });
                toast.success(_t(`basic:${value}_success`));
                bulkActionCallback?.();
                document.body.classList.remove('no-Events');
              })
              .catch(() => {
                bulkActionCallback?.();
                document.body.classList.remove('no-Events');
              });
          },
        );
      }
    };

    const isLivePaginationOn = tableMode === 'auto';

    const renderHeader = ({ children, name, columnsState }: any) => {
      return (
        <>
          <div className={styles.HeaderCell} id="primayMainTable">
            <label className="editTable-Checkbox" id="primayMainTable-checkbox">
              <CustomCheckbox
                name={name}
                checked={columnsState[name]}
                onChange={(e: any) => {
                  setShowedColumnsBackup(showedColumns);
                  setShowedColumns((oldVal: any) => ({
                    ...oldVal,
                    [e.target.name]: e.target.checked,
                  }));
                  setColumns(
                    getColumns({
                      draggable: true,
                      isEdit: true,
                      currentColumnsState: {
                        ...columnsState,
                        [e.target.name]: e.target.checked,
                      },
                      columns: activeColumns,
                    }),
                  );
                }}
              />
              <span />
            </label>
            <div>{children[0]}</div>
            <div>{children[1]}</div>
          </div>
        </>
      );
    };

    const getColumns = ({
      draggable,
      isEdit,
      currentColumnsState,
    }: {
      draggable: boolean;
      isEdit: boolean;
      currentColumnsState: any;
      columns: any[];
    }) => {
      return activeColumns?.map((column: any) => ({
        name: column?.name,
        type: column?.type,
        sortable: isEdit ? false : column.sortable ? true : false,
        draggable: draggable,
        render: column.render ? column.render : undefined,
        header: column?.header
          ? typeof column.header === 'object'
            ? column.header
            : column.header?.toUpperCase()
          : undefined,
        defaultWidth: column?.defaultWidth,
        minWidth: column?.minWidth,
        maxWidth: column?.maxWidth,
        defaultLocked: column?.defaultLocked,
        flex: column?.flex,
        width: column?.width,
        resizable: column?.resizable,
        visible: isEdit === true || currentColumnsState[column.name],
        showColumnMenuTool: false,
        sort: () => null,
        renderHeader: (props: any) =>
          isEdit
            ? renderHeader({ ...props, columnsState: currentColumnsState })
            : renderDefaultHeader({
                ...props,
                sortable: column.sortable,
                columnsState: currentColumnsState,
              }),
        defaultFlex: column.defaultWidth
          ? undefined
          : window.innerWidth >= 992 && 1,
      }));
    };

    // const [resizable, setResizable] = useState(true);
    const [Columns, setColumns] = useState(() =>
      getColumns({
        draggable,
        isEdit: isEditModeOn,
        currentColumnsState: showedColumns,
        columns: [],
      }),
    );
    const initialGridStyle = {
      minHeight: 860,
      margin: '10px 0',
    };

    const [gridStyle, setGridStyle] = useState(() => initialGridStyle);

    const stopLoadingAndScroll = useCallback((page: any) => {
      if (page === 1)
        scrollTo({
          behavior: 'smooth',
          top: 500,
        });
    }, []);

    const setViewModeState = useCallback((res: any) => {
      const currentLimit = rowsLimit
        ? res?.payload?.records?.length + 1
        : res?.payload?.limit;
      setCurrentDataSource(res.payload);
      if (tableMode !== 'auto') {
        setGridStyle({
          ...initialGridStyle,
          minHeight:
            currentLimit === 10
              ? 860
              : (currentLimit / 10) * getTableMinHeight(currentLimit) -
                (80 - currentLimit),
        });
      }
      //todo => add condition to prevent the limit which is bigger than the total records
    }, []);
    const formatDateTime = (d: Date | string, type: string) => {
      const date = format(new Date(d), 'yyyy-MM-dd').concat(
        type === 'end_date' ? 'T23:59:59.000Z' : 'T00:00:00.000Z',
      );
      return fromLocalToServerTimeZone(date);
    };

    const filtersToBase64 = (oldFilters: any, active?: string) => {
      const filtersKeys = Object.keys(oldFilters);
      const addedFilters: any = {};
      const excludeKeys = ['date', 'page', 'limit'];

      for (let index = 0; index < filtersKeys.length; index++) {
        const key = filtersKeys[index];
        const element = oldFilters[key];

        if (
          !Array.isArray(element) &&
          typeof element?.value === 'undefined' &&
          !excludeKeys.some((k) => key.includes(k))
        ) {
          addedFilters[key] = { label: element, value: element };
        } else {
          addedFilters[key] = element;
        }
      }
      if (!getSearchState()?.activeTab) {
        setSearchState({
          filters: customRequiredFilters,
          activeTab: activeTab,
        });
      }
      return {
        filters: addedFilters,
        activeTab: active ? active : activeTab,
      };
    };

    const _loadData = async (
      props: any,
      reset?: boolean,
      isBrowserFilter?: boolean,
    ) => {
      if (!moduleName) return [];
      const oldFilters = reset
        ? customRequiredFilters
        : { ...filters, ...customRequiredFilters };

      !isLivePaginationOn && onSelectionChange({ selected: [] });
      const recentPage = props.skip / limit + 1;
      const page = props?.page || 1;
      const addedFilters: any = {};
      const allFilters = isLivePaginationOn
        ? {
            limit: limit,
            order_column: props?.sortInfo?.name,
            type_of_sort:
              props?.sortInfo?.dir === 1
                ? 'desc'
                : props?.sortInfo?.dir === -1
                  ? 'asc'
                  : undefined,
            ...oldFilters,
            page: recentPage ? recentPage : page,
          }
        : {
            ...oldFilters,
            ...props,
            page: page,
            limit: props?.limit || limit,
          };
      if (!stopSearchFilters) {
        !isBrowserFilter &&
          setSearchState(
            filtersToBase64(
              handleCleanObjFromFalsyValues({
                ...allFilters,
                ...filters,
                page: page,
              }),
            ),
          );
      }

      const filtersKeys = Object.keys(
        handleCleanObjFromFalsyValues(allFilters),
      );

      for (let index = 0; index < filtersKeys.length; index++) {
        const key = filtersKeys[index];
        const element = allFilters[key];

        const isArrayOfLabels = Array.isArray(element)
          ? element?.some((item: any) => item?.label)
          : false;
        if (
          (key.includes('ids') && Array.isArray(element)) ||
          isArrayOfLabels
        ) {
          addedFilters[key] = element.map((item: any) => item?.value || item);
        } else addedFilters[key] = element;
        if (key.includes('date')) {
          addedFilters[key] = element ? formatDateTime(element, key) : element;
        }

        const isSingleId =
          key.includes('id') && !key.includes('ids') && Array.isArray(element);
        if (key.includes('percentage') || isSingleId)
          addedFilters[key] =
            typeof element?.[0] === 'number'
              ? element?.[0]
              : element?.[0]?.value !== undefined
                ? element?.[0]?.value
                : element?.value
                  ? element?.value
                  : element;

        if (
          initialFiltersKeys?.some((customKey: string) => customKey === key)
        ) {
          if (Array.isArray(element)) {
            addedFilters[key] = element.map((item: any) => item?.value || item);
          } else {
            addedFilters[key] = element?.value ? element?.value : element;
          }
        }
        if (
          isArrayOfLabels &&
          element?.some((e: any) => typeof e.value === 'boolean')
        ) {
          addedFilters[key] = element.map((item: any) => item?.value);
        }
        if (element?.value) {
          addedFilters[key] = element?.value ? element?.value : element;
        }
      }

      const result = (await loadTableMutate.mutateAsync({
        moduleName,
        data: addedFilters,
      })) as any;
      loadDataCallback?.(result);
      if (isLivePaginationOn) {
        currentPage.current = isNaN(recentPage) ? 1 : recentPage;
        if (result?.payload?.records?.length === 0) {
          return [];
        } else {
          if (props.backToViewMode) {
            currentPage.current = 1;
            setViewModeState(result);
            return [];
          }
          /*           if (result.payload.page !== result.payload.total_page) {
            lastHeightReached.current =
              result?.payload?.records?.length * 80 * result?.payload?.page + 7;
            setGridStyle((oldVal) => ({
              ...oldVal,
              minHeight: lastHeightReached.current,
            }));
          } else {
            setGridStyle((oldVal) => ({
              ...oldVal,
              minHeight: result?.payload?.total_record * 80 + 45,
            }));
          } */

          setCurrentDataSource(result?.payload);
          /*   stopLoadingAndScroll(currentPage.current); */
          return {
            data: result?.payload?.records,
            count: result?.payload?.total_record,
          };
        }
      } else {
        setViewModeState(result);
      }
    };
    const dataSource = useCallback(
      _loadData,

      [tableMode, filtersChanged],
    );
    const onPaginate = (event: React.ChangeEvent<unknown>, value: number) => {
      if (value !== currentDataSource?.page) {
        onChangeFilter('page')(value);
        const newFilters = formatFilters();
        const currentFilters =
          submitFiltersCounter.current === 0
            ? { ...customRequiredFilters }
            : newFilters;
        _loadData({ ...currentFilters, ...sortInfo, page: value, limit });
        // if(noScroll)
        scrollTo({
          behavior: 'smooth',
          top: scrollToTopOfTableOnly ? tableRef?.current?.offsetTop - 53 : 0,
        });
      }
    };
    const onSubmit = () => {
      const newFilters = formatFilters();
      submitFiltersCounter.current = submitFiltersCounter.current + 1;
      setTimeout(() => {
        setFiltersChanged((e) => e + 1);
      }, 0);

      if (!isLivePaginationOn)
        _loadData({
          ...newFilters,
          ...sortInfo,
          page: 1,
          limit,
          trashed: initialFilters.trashed ? initialFilters.trashed : undefined,
          ...customRequiredFilters,
        });
    };

    const onReset = () => {
      const cleanedInitialFilters = resetFilters();
      submitFiltersCounter.current = 0;
      const isCountry = cleanedInitialFilters?.country_ids
        ? { country_ids: null }
        : null;
      setFiltersChanged((e) => e + 1);
      // re remove falsy values from the filter object here
      const filtersObj = handleCleanObjFromFalsyValues({
        page: filters.page,
        limit,
        ...isCountry,
        start_date: null,
        end_date: null,
        ...cleanedInitialFilters,
        ...sortInfo,
      });
      setSearchState({});
      if (!isLivePaginationOn) _loadData(filtersObj, true, true);
    };

    const onSelectionChange = useCallback(({ selected }: any) => {
      const selectedMap: any = {};
      for (const key in selected) {
        const element = selected[key];
        if (!element?.is_default && !element.is_merged) {
          selectedMap[element?.id] = element;
        }
      }
      setSelected(selectedMap);
    }, []);

    const _handleBulkAction = async (value: string) => {
      const selectedItems = Object.keys(selected).map((item) => +item);

      switch (value) {
        case 'compare':
          if (selectedItems.length > 1) {
            if (selectedItems.length > 5) {
              toast.error(_t(`basic:cannot_compare`));
            } else {
              const firstFiveItems = selectedItems.map(
                (item) => selected[item],
              );
              history.push(compareUrl as any, firstFiveItems);
            }
          }
          break;
        case 'permission':
          {
            selectedItems.length && currentTabRef?.current.showModal();
          }
          break;
        case 'unnecessary':
          {
            selectedItems.length &&
              checkUnnecessaryIsAdd(selectedItems, 'unnecessary');
          }

          break;
        case 'important':
          {
            selectedItems.length &&
              checkImportantInfluencer(selectedItems, 'mark-important');
          }
          break;
        case 'unimportant':
          {
            selectedItems.length &&
              checkUnImportantInfluencer(selectedItems, 'mark-unimportant');
          }
          break;
        case 'attach':
          {
            if (
              selectedItems.length &&
              (await bulkActionMethod?.(selectedItems, selected))
            ) {
              showModal();
            }
          }
          break;
        case 'merge':
          {
            if (currentModule === 'hashtags' && selectedItems.length > 0) {
              currentTabRef?.current?.showSecondModal();
              return;
            }
            if (
              selectedItems.length === 1 ||
              (currentModule === 'news' && selectedItems.length > 1)
            ) {
              currentTabRef?.current?.showSecondModal();
              return;
            }
            if (selectedItems.length > 1) currentTabRef?.current?.showModal();
          }
          break;
        case 'regrab':
          {
            if (selectedItems.length > 0) {
              regrabBulkActionMutation.isLoading
                ? toast.error(_t('basic:cant_regrab'))
                : openConfirmationPopup(
                    modal,
                    'regrab',
                    actionModule,
                    sumOfActionModule,
                    async () => {
                      await regrabBulkActionMutation
                        .mutateAsync({
                          id: selectedItems,
                          module:
                            currentModule == 'news' &&
                            activeTab !== 'ungrabbed_articles'
                              ? 'news'
                              : currentModule == 'news' &&
                                  activeTab == 'ungrabbed_articles'
                                ? 'ungrabed_article'
                                : currentModule == 'tweets'
                                  ? 'tweets'
                                  : currentModule == 'posts'
                                    ? activeTab === 'tiktok_posts'
                                      ? 'tiktok_posts'
                                      : activeTab === 'tweets'
                                        ? 'twitter_posts'
                                        : activeTab === 'linkedin_posts'
                                          ? 'linkedin_posts'
                                          : activeTab === 'youtube_posts' || activeTab === 'trending_youtube'
                                          ? 'youtube_posts'
                                          : activeTab === 'trending'
                                            ? 'tiktok_posts_trending'
                                            : 'posts'
                                    : 'hashtags',
                        })
                        .then(() =>
                          _loadData({ ...filters, ...sortInfo, limit }),
                        );
                      setSelected({});
                    },
                    undefined,
                    undefined,
                    true,
                    _t('basic:ok'),
                    `${_t('basic:working_on_grabbing')} ${
                      actionModule ? actionModule : ''
                    } / ${sumOfActionModule ? sumOfActionModule : ''} (${_t(
                      'basic:you_will_be_notified',
                    )})`,
                  );
            }
          }
          break;
        case 'sync':
          {
            if (selectedItems.length > 0) {
              syncBulkActionMutation.isLoading
                ? toast.error(_t('basic:cant_sync'))
                : openConfirmationPopup(
                    modal,
                    'sync',
                    actionModule,
                    sumOfActionModule,
                    async () => {
                      await syncBulkActionMutation
                        .mutateAsync({
                          id: selectedItems,
                        })
                        .then(() =>
                          _loadData({ ...filters, ...sortInfo, limit }),
                        );
                      setSelected({});
                    },
                    undefined,
                    undefined,
                    true,
                    _t('basic:ok'),
                    `${_t('basic:working_on_syncing')} ${
                      actionModule ? actionModule : ''
                    } / ${sumOfActionModule ? sumOfActionModule : ''} (${_t(
                      'basic:you_will_be_notified',
                    )})`,
                  );
            }
          }
          break;
        case 'partial-sync':
          {
            if (selectedItems.length > 0) {
              partialSyncBulkActionMutation.isLoading
                ? toast.error(_t('basic:cant_sync'))
                : openConfirmationPopup(
                    modal,
                    'sync',
                    actionModule,
                    sumOfActionModule,
                    async () => {
                      await partialSyncBulkActionMutation
                        .mutateAsync({
                          id: selectedItems,
                        })
                        .then(() =>
                          _loadData({ ...filters, ...sortInfo, limit }),
                        );
                      setSelected({});
                    },
                    undefined,
                    undefined,
                    true,
                    _t('basic:ok'),
                    `${_t('basic:working_on_syncing')} ${
                      actionModule ? actionModule : ''
                    } / ${sumOfActionModule ? sumOfActionModule : ''} (${_t(
                      'basic:you_will_be_notified',
                    )})`,
                  );
            }
          }
          break;
        case 'check':
          {
            if (selectedItems.length > 0) {
              checkBulkActionMutation.isLoading
                ? toast.error(_t('basic:cant_check'))
                : openConfirmationPopup(
                    modal,
                    'check',
                    actionModule,
                    sumOfActionModule,
                    async () => {
                      await checkBulkActionMutation
                        .mutateAsync({
                          id: selectedItems,
                        })
                        .then(() =>
                          _loadData({ ...filters, ...sortInfo, limit }),
                        );
                      setSelected({});
                    },
                    undefined,
                    undefined,
                    true,
                    _t('basic:ok'),
                    `${_t('basic:working_on_checking')} ${
                      actionModule ? actionModule : ''
                    } / ${sumOfActionModule ? sumOfActionModule : ''} (${_t(
                      'basic:you_will_be_notified',
                    )})`,
                  );
            }
          }
          break;
        case 'linkage':
          {
            selectedItems.length && bulkActionMethod?.(selectedItems, selected);
          }
          break;
        case 'bookmark':
          {
            // I made it for now cause the system crash ,it will be changed
            openConfirmationPopup(
              modal,
              value,
              actionModule,
              sumOfActionModule,
              () => {
                // _loadData({ ...filters, ...sortInfo, limit });
                // toast.success(_t(`basic:${value}_success`));
                bulkActionCallback?.();
              },
            );
          }
          break;
        case 'fastReport':
          {
            if (selectedItems.length > 0) {
              document.body.classList.add('no-Events');
              await fastReportBulkActionHandler?.(
                value,
                selectedItems,
                sortInfo,
                activeTab,
              );
              /*  _loadData({ ...filters, ...sortInfo, limit }); */
              bulkActionCallback?.();
              document.body.classList.remove('no-Events');
            }
          }
          break;
        default:
          if (selectedItems.length > 0) {
            if (
              !loadTableMutate.isLoading &&
              !bulkActionMutate.isLoading &&
              !deleteBulkActionMutate.isLoading
            )
              if (
                value === 'restore' ||
                value === 'active' ||
                value === 'unblock'
              ) {
                bulkActionMutate
                  .mutateAsync({
                    action: value,
                    moduleName: bulkActionsKey,
                    ids: selectedItems,
                    bulkFilter,
                  })
                  .then(() => {
                    _loadData({ ...filters, ...sortInfo, limit });
                    toast.success(_t(`basic:${value}_success`));
                    bulkActionCallback?.();
                  });
              } else {
                const hasSubcategory = selectedHasKey('sub_category_count');
                /*   const statusIsInprogress =
                  selectedIsProgress('grabbing_status'); */
                if (
                  hasSubcategory &&
                  (actionModule?.toLowerCase() ===
                    _t('basic:influencer_category') ||
                    actionModule?.toLowerCase() ===
                      _t('basic:inf_categories') ||
                    actionModule?.toLowerCase() ===
                      _t('basic:entities_category') ||
                    actionModule?.toLowerCase() ===
                      _t('basic:entities_categories') ||
                    actionModule?.toLowerCase() === _t('news:news_category') ||
                    actionModule?.toLowerCase() ===
                      _t('news:news_categories') ||
                    actionModule?.toLowerCase() ===
                      _t('topics:topic_category') ||
                    actionModule?.toLowerCase() ===
                      _t('topics:topics_categories'))
                ) {
                  value = 'trash_category';
                }
                /*         if (statusIsInprogress) {
                  value = 'delete_incomplete_influencer';
                } */
                openConfirmationPopup(
                  modal,
                  value,
                  actionModule,
                  sumOfActionModule,
                  async () => {
                    if (
                      (value === 'block' || value === 'trash') &&
                      moduleName?.includes('users')
                    ) {
                      if (
                        selectedItems?.includes(
                          user?.user_id ? +user?.user_id : -1,
                        )
                      )
                        return toast.error(
                          _t('users:sorry_permitted_for_the_user'),
                        );
                    }
                    if (value === 'delete_incomplete_influencer') {
                      return toast.error(_t('influencers:you_can_not_delete'));
                    }

                    try {
                      switch (value) {
                        case 'delete':
                          document.body.classList.add('no-Events');
                          await deleteBulkActionMutate.mutateAsync({
                            action: value,
                            moduleName: bulkActionsKey,
                            ids: selectedItems,
                            bulkFilter,
                            activeTab,
                          });
                          break;
                        case 'trash_category':
                          document.body.classList.add('no-Events');
                          await bulkActionMutate.mutateAsync({
                            action: 'trash',
                            moduleName: bulkActionsKey,
                            ids: selectedItems,
                            bulkFilter,
                          });
                          break;
                        case 'delete_incomplete_influencer':
                          document.body.classList.add('no-Events');
                          await bulkActionMutate.mutateAsync({
                            action: 'delete',
                            moduleName: bulkActionsKey,
                            ids: selectedItems,
                            bulkFilter,
                          });
                          break;
                        default:
                          document.body.classList.add('no-Events');
                          await bulkActionMutate.mutateAsync({
                            action: value,
                            moduleName: bulkActionsKey,
                            ids: selectedItems,
                            bulkFilter,
                          });
                          break;
                      }

                      await _loadData({ ...filters, ...sortInfo, limit });
                      document.body.classList.remove('no-Events');
                      toast.success(
                        _t(
                          `basic:${
                            value === 'trash-or-unnecessary' ? 'hide' : value
                          }_success`,
                        ),
                      );
                      bulkActionCallback?.();
                    } catch (error) {
                      document.body.classList.remove('no-Events');
                    }
                  },
                );
              }
          }
      }
    };

    const filtersInputs = [
      {
        onChange: onChangeFilter('hashtag'),
        type: 'autocomplete',
        icon: <HashtagsIcon />,
        placeholder: _t('trends:searchBy_hashtagName'),
        value: filters.hashtag,
        moduleName: apiRoutes().hashtagsAutoComplete,
        size: 'large',
        keyExtractor: 'label',
      },
      {
        onChange: onChangeFilter('country_ids'),
        type: 'select',
        icon: <CountryIcon />,
        placeholder: _t('trends:Country'),
        options: countriesOptions,
        isMulti: true,
        value: filters.country_ids,
        size: 'large',
      },
      {
        onChange: onChangeFilter('platform_ids'),
        type: 'select',
        icon: <PlatformIcon />,
        placeholder: _t('trends:Platforms'),
        options: platforms?.platformsOptions,
        isMulti: true,
        value: filters.platform_ids,
        size: 'large',
      },
      {
        onChange: onChangeFilter('start_date'),
        type: 'datepicker',
        icon: <DateFrom />,
        placeholder: _t('basic:from'),
        value: filters.start_date,
        size: 'small',
      },
      {
        onChange: onChangeFilter('end_date'),
        type: 'datepicker',
        icon: <DateTo />,
        placeholder: _t('basic:to'),
        value: filters.end_date,
        minDate: filters.start_date,
        size: 'small',
      },
    ];
    const customFilters = moduleFilters?.map((input) => {
      const isDatePicker = input.type === 'datepicker';
      const isSourceEdition = input.name === 'source_edition_ids';
      const isOrganization = input.module === 'organizations';

      let customFilters = {};
      let minMaxDate = {};

      if (isOrganization) {
        customFilters = { organization_id: filters.organization_id };
      }

      if (isSourceEdition) {
        customFilters = {
          source_ids: Array.isArray(filters?.source_ids)
            ? filters?.source_ids?.map((item: any) => item.value)
            : [],
        };
      }

      if (isDatePicker) {
        const isStartDate = input.name === 'start_date';
        if (isStartDate)
          minMaxDate = {
            maxDate: handleGetDates.end_date,
          };
        else
          minMaxDate = {
            minDate: handleGetDates.start_date,
          };
      }

      return {
        customFilters,
        ...input,
        onChange: onChangeFilter(input.name),
        moduleName:
          input.moduleName !== undefined ? input.moduleName : undefined,
        value: filters[input.name],
        ...minMaxDate,
      };
    });
    const scrollProps = {
      ...ReactDataGrid.defaultProps.scrollProps,
      scrollThumbWidth: 0,
      scrollThumbOverWidth: 0,
    };

    const _handleSort = (value: any) => {
      if (value === null) {
        _loadData({ ...filters, limit, page: 1 });
        setSortInfo({ order_column: '', type_of_sort: '' });
      } else {
        const { dir, name } = value;
        const sortInformation = {
          order_column: name,
          type_of_sort: dir === 1 ? 'desc' : 'asc',
        };
        _loadData({
          ...filters,
          ...sortInformation,
          limit,
          page: 1,
        });
        setSortInfo(sortInformation);
      }
    };
    const bulkActions = [
      ...bulkActionOpts?.map(
        (option) =>
          bulkActionMap.filter((mapOpts) => mapOpts.value === option)?.[0],
      ),
    ];
    useImperativeHandle(
      ref,
      () => ({
        filters: filters,
        onChangeFilter: onChangeFilter,
        onSubmit: onSubmit,
        activeTab,
        setActiveTab,
        loadData: (updateFilters = {}) =>
          _loadData({
            ...filters,
            ...sortInfo,
            ...updateFilters,
            ...customRequiredFilters,
          }),
        currentDataSource,
      }),

      [{}],
    );

    useEffect(() => {
      if (!parentPassedData && !stopLoadingFirstMount) {
        _loadData(
          {
            ...filters,
            ...sortInfo,
            ...getSearchState()?.filters,
            limit,
          },
          undefined,
          true,
        );
      }
    }, []);

    useBrowserBack({
      onBrowserHistoryChange: () => {
        setActiveTab?.(
          getSearchState()?.activeTab.length
            ? getSearchState()?.activeTab
            : defaultTab
              ? defaultTab
              : typeof tabs?.[0] !== 'string'
                ? tabs?.[0]?.key
                : '',
        );
        const searchData = getSearchState()?.filters
          ? getSearchState()?.filters
          : {};
        _loadData(
          {
            ...searchData,
            ...filters,
            ...sortInfo,
            limit,
          },
          undefined,
          true,
        );
      },
    });
    const handleSetNewOrder = (newOrder: any) => {
      setColumnOrder({
        oldOrder: columnOrder.currentOrder,
        currentOrder: newOrder,
      });
    };
    const [showPopup, setShowPopup] = useState(false);
    const showModal = () => {
      setShowPopup(true);
    };
    const closeModal = () => {
      setShowPopup(false);
    };
    const BulkActionPopup = useMemo(() => {
      if (typeof tabBulkActionPopup === 'string') return;
      return (
        tabBulkActionPopup &&
        React.Children.map(
          (tabBulkActionPopup as any).props.children,
          (child) => {
            const selectedItems = Object.keys(selected).map((item) => +item);
            const items = Object.keys(selected).map((item) => selected[item]);
            return cloneElement(child, {
              selectedItems,
              setSelected,
              items,
              showPopup: showPopup,
              closeModal: closeModal,
            });
          },
        )
      );
    }, [selected, tabBulkActionPopup, showPopup]);

    const isTableDataReadyToBeRendered = isLivePaginationOn
      ? dataSource
      : currentDataSource;
    if (
      hideIfNoData &&
      Array.isArray(currentDataSource?.records) &&
      currentDataSource?.records?.length === 0
    ) {
      return <></>;
    }

    return (
      <>
        <Grid id={compId} {...grid}>
          <Grid
            container
            className={styles.TableContainer}
            id="primayMainTable-container"
            ref={tableRef}
          >
            {isFiltered && (
              <Grid item sm={12}>
                <MainFilter
                  inputs={customFilters || filtersInputs}
                  onSubmit={onSubmit}
                  onReset={onReset}
                  isLoading={
                    loadTableMutate.isLoading ||
                    bulkActionMutate.isLoading ||
                    deleteBulkActionMutate.isLoading
                  }
                  removeFilterMr={removeFilterMr}
                />
              </Grid>
            )}
            {children && (
              <Grid item sm={12}>
                {children}
              </Grid>
            )}
            <Grid item sm={12}>
              <Grid
                container
                className={`${styles.PrimaryTable} ${
                  removeTopPadding ? styles.removeTopPadding : ''
                }`}
                id="primaryTable-editMode"
              >
                {isEditModeOn ? (
                  <div
                    className={styles.PrimaryTable__SaveControlTable}
                    data-testid="edit-mode-holder"
                  >
                    <div className={styles.PrimaryTable__EditModeText}>
                      {_t('basic:edit_mode_on')}
                    </div>
                    <div
                      className={styles.Controls}
                      id="primayMainTable-Controls"
                    >
                      <button
                        className="text-btn"
                        id="mainTable-editMode-saveIcon"
                        onClick={
                          isThereASelectedColumn(showedColumns)
                            ? () => {
                                const sumSizes =
                                  columnsSizes
                                    ?.filter(
                                      (item: any) => showedColumns[item?.name],
                                    )
                                    ?.map((item: any) => item?.width)
                                    ?.filter(Boolean)
                                    ?.reduce((a: any, b: any) => a + b, 0) + 70;

                                const convertSizes = columnsSizes?.map(
                                  (item: any) => {
                                    return columnOrder.currentOrder[
                                      columnOrder?.currentOrder?.length - 1
                                    ] === item?.name &&
                                      sumSizes < tableRef?.current?.clientWidth
                                      ? {
                                          ...item,
                                          defaultFlex: 1,
                                        }
                                      : { ...item, defaultFlex: undefined };
                                  },
                                );

                                setDraggable(false);
                                setIsEditModeOn(false);
                                setShowedColumnsBackup(showedColumns);

                                setInLocalStorage(
                                  `${baseTableName}_columns_sizes`,
                                  convertSizes,
                                );

                                setInLocalStorage(
                                  `${baseTableName}_columns_order`,
                                  columnOrder.currentOrder,
                                );
                                setInLocalStorage(
                                  `${baseTableName}_columns_visibility`,
                                  showedColumns,
                                );
                                setColumnOrder({
                                  ...columnOrder,
                                  currentOrder: columnOrder.currentOrder,
                                });
                                setColumns(
                                  getColumns({
                                    draggable: false,
                                    isEdit: false,
                                    currentColumnsState: showedColumns,
                                    columns: activeColumns,
                                  }),
                                );
                                window.location.reload();
                              }
                            : () => toast.error(_t('basic:atleastonecolumn'))
                        }
                      >
                        <SaveIcon />
                      </button>
                      <button
                        onClick={() => {
                          setIsEditModeOn(false);
                          setColumnOrder({
                            ...columnOrder,
                            currentOrder: columnOrder.oldOrder,
                          });
                          setTableKey((n) => `${+n + 1}`);
                          setShowedColumns(showedColumnsBackup);
                          setColumns(
                            getColumns({
                              draggable: false,
                              isEdit: false,
                              currentColumnsState: showedColumnsBackup,
                              columns: activeColumns,
                            }),
                          );
                        }}
                        id="mainTable-editMode-cancelIcon"
                      >
                        <CancelIcon />
                      </button>
                    </div>
                  </div>
                ) : null}
                {!hideTopBar && (
                  <Grid
                    container
                    justifyContent="space-between"
                    direction="row"
                    data-testid="top-bar-holder"
                    id="primTable-topBar"
                  >
                    <Grid
                      item
                      sm={12}
                      lg={12}
                      className={styles.PrimaryTable__BulkActions}
                    >
                      {title ? (
                        <h3>{title}</h3>
                      ) : (
                        <Grid
                          item
                          sm={7}
                          className={styles.PrimaryTable__BulkActionsPartOne}
                          id="primTable-bulkAction"
                        >
                          {!hideBulkActions ? (
                            <div
                              className={styles.bulkActionContainer}
                              data-testid="bulk-action-holder"
                            >
                              <SelectInput
                                placeholder={_t('basic:bulk_action')}
                                value={{
                                  label: _t('basic:bulk_action'),
                                  value: _t('basic:bulk_action'),
                                }}
                                options={bulkActions}
                                onChange={(e) => _handleBulkAction(e.value)}
                                showInitialValue
                                name={''}
                              />
                            </div>
                          ) : null}

                          {!hideAllTap && (
                            <div
                              className={styles.tabs_parent}
                              data-testid="tabs-holder"
                            >
                              {tabs?.map((tab) => (
                                <div
                                  id="primayMainTable-Tab"
                                  key={typeof tab !== 'string' ? tab.key : ''}
                                  style={{
                                    textTransform: 'uppercase',
                                  }}
                                  className={`${
                                    styles.PrimaryTable__BulkActionsPartOne__AllTabs
                                  } ${
                                    styles.PrimaryTable__BulkActionsPartOne__Tab
                                  } ${
                                    typeof tab !== 'string' &&
                                    tab?.key === activeTab
                                      ? 'active'
                                      : null
                                  }`}
                                  onClick={() => {
                                    setActiveTab?.(
                                      typeof tab !== 'string' ? tab?.key : '',
                                    );
                                    if (
                                      typeof tab !== 'string' &&
                                      tab?.key !== activeTab
                                    )
                                      setSearchState(
                                        filtersToBase64({}, tab?.key),
                                      );
                                  }}
                                >
                                  {typeof tab !== 'string' ? tab?.name : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </Grid>
                      )}
                      {showRegrabIcon && (
                        <ToolTipComp
                          title={
                            regrabIconTooltipTitle &&
                            `${_t('basic:last_grabbed_on')} : ${dateTimeFormatter?.date(
                              regrabIconTooltipTitle,
                            )} ${_t('basic:at')} ${dateTimeFormatter?.time(regrabIconTooltipTitle)}`
                          }
                        >
                          <RestartIcon
                            className="cursor-pointer"
                            onClick={() => regrabIconFunction?.()}
                          />
                        </ToolTipComp>
                      )}
                      <TableControls
                        {...{
                          title: title ? title : '',
                          currentDataSource,
                          tableMode,
                          limit,
                          setTableOpts,
                          setColumns,
                          setIsEditModeOn,
                          getColumns,
                          showedColumns,
                          columns,
                          setLimit,
                          filters,
                          sortInfo,
                          isLivePaginationOn,
                          currentPage: currentPage.current,
                          baseTableName,
                        }}
                        loadData={_loadData}
                        compId={compId || ''}
                      />
                    </Grid>
                  </Grid>
                )}
                <Grid item sm={12} lg={12} id={`primTable-content`}>
                  {isTableDataReadyToBeRendered ? (
                    <div
                      data-testid="primary-main-table-holder"
                      id="primary-main-table-holder"
                    >
                      <ReactDataGrid
                        licenseKey={process.env.REACT_APP_DATA_GRID_LICENSE}
                        key={tableKey}
                        loading={
                          isLivePaginationOn
                            ? undefined
                            : loadTableMutate.isLoading ||
                              bulkActionMutate.isLoading ||
                              deleteBulkActionMutate.isLoading ||
                              isLoading
                        }
                        idProperty="id"
                        columns={currentDataSource?.records ? Columns : []}
                        rowClassName="mainTable-row"
                        dataSource={
                          isLivePaginationOn
                            ? (dataSource as any)
                            : currentDataSource?.records
                              ? currentDataSource?.records
                              : []
                        }
                        defaultSortInfo={
                          defaultSortInfo
                            ? defaultSortInfo
                            : { name: '', dir: 0 }
                        }
                        columnOrder={columnOrder.currentOrder}
                        onBatchColumnResize={(sizes: any) => {
                          setColumnsSizes((prevSizes: any) => {
                            const newSizes = sizes?.map(
                              ({ column, width, flex, size }: any) => ({
                                name: column?.name,
                                width: width || flex || size,
                              }),
                            );

                            const newValues = prevSizes?.map(
                              (prevSize: any) => ({
                                ...prevSize,
                                width:
                                  columns?.find(
                                    (column: { name: any }) =>
                                      column.name === prevSize.name,
                                  )?.defaultWidth ||
                                  newSizes?.find(
                                    ({ name }: any) => name === prevSize?.name,
                                  )?.width ||
                                  prevSize?.width,
                              }),
                            );

                            return !prevSizes?.length ? newSizes : newValues;
                          });
                        }}
                        onColumnOrderChange={handleSetNewOrder}
                        style={
                          isLivePaginationOn
                            ? { minHeight: '790px' }
                            : gridStyle.minHeight < 500
                              ? { ...gridStyle, minHeight: '50em' }
                              : Array.isArray(currentDataSource?.records) &&
                                  currentDataSource?.records?.length === 0
                                ? { ...gridStyle, minHeight: '860px' }
                                : gridStyle
                        }
                        rowSelection={{
                          showCheckbox: true,
                        }}
                        checkboxColumn={
                          hideCheckbox ? false : (checkboxColumn as any)
                        }
                        rtl={!isEn}
                        defaultLimit={limit}
                        scrollProps={scrollProps}
                        pagination={isLivePaginationOn ? true : undefined}
                        livePagination={isLivePaginationOn ? true : undefined}
                        selected={selected}
                        onSelectionChange={onSelectionChange}
                        showHoverRows={false}
                        showCellBorders={'horizontal'}
                        rowHeight={80}
                        showZebraRows={false}
                        checkboxOnlyRowSelect={true}
                        treeColumn={treeColumn ? treeColumn : undefined}
                        renderSortTool={renderSortTool}
                        onSortInfoChange={_handleSort}
                        emptyText={emptyText}
                        loadingText={_t('basic:is_loading')}
                        activateRowOnFocus={false}
                        showActiveRowIndicator={false}
                        autoCheckboxColumn={true}
                        shareSpaceOnResize={!isEditModeOn ? true : false}
                        resizable={!isEditModeOn ? false : true}
                      />
                      {!hidePagination && (
                        <TablePagination
                          {...{
                            onPaginate,
                            currentDataSource,
                            tableMode,
                          }}
                        />
                      )}
                    </div>
                  ) : loadTableMutate.isError ? (
                    <div className={styles.Error}>
                      {_t('basic:still_retrieve_data_from_database')}
                    </div>
                  ) : (
                    !hideLoader && (
                      <div className="loader-parent">
                        <Loader />
                      </div>
                    )
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {additionalComponents ? (
            <div className="position-relative">{additionalComponents}</div>
          ) : (
            <></>
          )}
        </Grid>
        {BulkActionPopup}
      </>
    );
  },
);

export default PrimaryMainTable;
